(function () {
  'use strict';

  const VAT_OPTION_NAME = 'VAT Exemption';
  const VAT_STORAGE_KEY = 'vatExempt';
  const VAT_REASON_KEY = 'vatExemptReason';

  function isVatExempt() {
    return localStorage.getItem(VAT_STORAGE_KEY) === 'true';
  }

  /**
   * Formats a price in pence using the store's active currency via Intl.NumberFormat.
   * Uses window.Shopify.currency.active for the correct currency code (e.g. "GBP").
   */
  function formatMoney(pence) {
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'GBP';
    return new Intl.NumberFormat('en-GB', {
      style: 'currency',
      currency: currency
    }).format(pence / 100);
  }

  /**
   * Returns all variants for this product from the JSON block output by
   * variant-selector.liquid.
   */
  function getVariantsFromDom(pickerEl) {
    var script = pickerEl.querySelector('script[data-vat-variants]');
    if (!script) return null;
    try {
      return JSON.parse(script.textContent);
    } catch (e) {
      console.warn('[VAT] Failed to parse variants JSON:', e);
      return null;
    }
  }

  /**
   * Returns the index of the "VAT Exemption" option among all fieldsets in the picker.
   */
  function getVatOptionIndex(pickerEl) {
    var index = 0;
    for (var fieldset of pickerEl.querySelectorAll('fieldset.variant-selector')) {
      var legend = fieldset.querySelector('legend');
      if (legend) {
        var text = legend.textContent.trim().split(':')[0].trim();
        if (text === VAT_OPTION_NAME) return index;
      }
      index++;
    }
    return -1;
  }

  /**
   * Returns the currently-selected option value for each fieldset (one entry per option).
   */
  function getSelectedOptions(pickerEl) {
    return Array.from(pickerEl.querySelectorAll('fieldset.variant-selector')).map(function (fs) {
      var checked = fs.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  /**
   * Finds the variant whose options match selectedOptions in every position
   * except vatIndex, where it must equal targetVatValue.
   */
  function findTargetVariant(variants, selectedOptions, vatIndex, targetVatValue) {
    return variants.find(function (v) {
      return v.options.every(function (val, idx) {
        return idx === vatIndex ? val === targetVatValue : val === selectedOptions[idx];
      });
    }) || null;
  }

  /**
   * Returns the nearest product-scoped ancestor element.
   */
  function getProductRoot(el) {
    return (
      el.closest('main-product') ||
      el.closest('product-card-quick-add') ||
      el.closest('[data-section-id]') ||
      document
    );
  }

  /**
   * Resolves the sibling variant that matches currentVariantId's option
   * values except for the VAT Exemption position, which is forced to the
   * given exemption state. Returns null when the product has no VAT
   * Exemption option, the current variant can't be found, it's already on
   * the correct side, or no matching sibling exists (rental-length variants
   * only ever pair with "No").
   *
   * Exposed on window.vatExemption so other modules — e.g. rental-picker.js,
   * which caches a Buy variant id on its own "Buy" radio and can lose track
   * of the shopper's VAT preference after a detour through a VAT-less
   * rental-length selection — can re-apply that preference without
   * duplicating this lookup.
   *
   * @param {Element} root - main-product (or similar) ancestor to search within
   * @param {string|number} currentVariantId
   * @param {boolean} isExempt
   * @returns {object|null} the target variant, or null
   */
  function resolveVariantIdForPurchaseType(root, currentVariantId, isExempt) {
    var picker = root && root.querySelector('variant-option-picker[data-vat-exempt="true"]');
    if (!picker) return null;

    var variants = getVariantsFromDom(picker);
    if (!variants) return null;

    var vatIndex = getVatOptionIndex(picker);
    if (vatIndex === -1) return null;

    var current = variants.find(function (v) { return String(v.id) === String(currentVariantId); });
    if (!current) return null;

    var targetVatValue = isExempt ? 'Yes' : 'No';
    if (current.options[vatIndex] === targetVatValue) return null;

    return findTargetVariant(variants, current.options, vatIndex, targetVatValue);
  }

  window.vatExemption = window.vatExemption || {};
  window.vatExemption.resolveVariantIdForPurchaseType = resolveVariantIdForPurchaseType;

  /**
   * Sets or clears the hidden [property] input that carries "VAT Exempt: Yes"
   * through to the cart line item via product-quick-add.js.
   */
  function updateVatProperty(root, isExempt) {
    var input = root.querySelector('[data-vat-exempt-property]');
    if (input) input.value = isExempt ? 'VAT Exempt' : '';
  }

  /**
   * Returns the customer-facing price for a variant fetched from /products/{handle}.js.
   * That JSON always carries the raw, ex-VAT stored price — add 20% unless this is the
   * "VAT Exemption: Yes" variant, which is already the price the exempt customer is charged.
   */
  function displayPriceFor(variant, isExempt) {
    return isExempt ? variant.price : Math.round(variant.price * (window.Theme?.vatMultiplier ?? 1.2));
  }

  window.vatExemption.displayPriceFor = displayPriceFor;

  /**
   * Updates the ATC button's variant ID inputs and price label, the Klarna
   * installment label, and the hidden selling_plan input, to reflect the
   * given variant and VAT-exemption state.
   *
   * The selling_plan sync matters once an Auto-renew variant can be VAT
   * exempt: Auto-renew/No and Auto-renew/Yes each carry their own selling
   * plan (see rental-selector.liquid), so swapping the variant id alone
   * without also updating the hidden selling_plan input would submit the
   * new variant against the OLD plan — for any other purchase type (Buy,
   * fixed length, which have no selling plan) this simply clears the field.
   */
  function updateAtcButton(root, variant, isExempt) {
    var idInput = root.querySelector('input[name="id"]');
    if (idInput) idInput.value = String(variant.id);

    var submitInput = root.querySelector('input[add-to-cart]');
    if (submitInput) submitInput.dataset.id = String(variant.id);

    var sellingPlanInput = root.querySelector('input[name="selling_plan"]');
    if (sellingPlanInput) {
      var allocation = variant.selling_plan_allocations && variant.selling_plan_allocations[0];
      sellingPlanInput.value = allocation ? String(allocation.selling_plan_id) : '';
    }

    var displayPrice = displayPriceFor(variant, isExempt);

    var priceSpan = root.querySelector('[data-variant-price-submit]');
    if (priceSpan) {
      priceSpan.dataset.price = String(displayPrice);
      priceSpan.textContent = '- ' + formatMoney(displayPrice);
    }

    var klarnaSpan = root.querySelector('[data-klarna-price-submit]');
    if (klarnaSpan) {
      klarnaSpan.dataset.price = String(displayPrice);
      klarnaSpan.textContent = formatMoney(Math.round(displayPrice / 3));
    }
  }

  /**
   * Updates rental-picker's own Buy header price and every fixed-length
   * rental radio's price, syncing their cached variant id / price to the
   * shopper's VAT-exemption state.
   *
   * Both are resolved independently of whatever is currently selected on the
   * page: Buy and each fixed length are distinct purchase options shown
   * alongside whichever one is actually active, so their prices must stay
   * correct regardless of the active selection — e.g. landing on a product
   * with Auto-renew (or nothing) selected as the default must still show
   * every fixed-length option's price correctly once the shopper opens that
   * accordion, not just whichever one happens to be checked.
   *
   * Each one anchors on its own cached variant id (always a real, resolvable
   * "<length> / No" or "<length> / Yes" variant), not the page's overall
   * selected variant.
   *
   * Auto-renew (recommended) is also synced, but only its checkout-critical
   * fields (variant id, selling plan id, initial charge) — see the comment
   * on that block below for why its visible price caption is left alone.
   *
   * A no-op for any part where no rental-picker/matching input/VAT-exempt
   * sibling exists (regular, non-rental, or not-yet-VAT-exempt products).
   */
  function updateRentalPlanDisplay(pickerEl, isExempt) {
    var root = getProductRoot(pickerEl);
    var picker = root.querySelector('rental-picker');
    if (!picker) return;

    var variants = getVariantsFromDom(pickerEl);
    if (!variants) return;

    var vatIndex = getVatOptionIndex(pickerEl);
    if (vatIndex === -1) return;

    var targetVatValue = isExempt ? 'Yes' : 'No';

    function resolveSibling(currentVariantId) {
      var current = variants.find(function (v) { return String(v.id) === String(currentVariantId); });
      if (!current) return null;
      if (current.options[vatIndex] === targetVatValue) return current;
      return findTargetVariant(variants, current.options, vatIndex, targetVatValue);
    }

    var buyInput = picker.querySelector('[data-rental-buy-input]');
    if (buyInput) {
      var buyVariant = resolveSibling(buyInput.dataset.variantId);
      if (buyVariant) {
        var buyPrice = displayPriceFor(buyVariant, isExempt);
        buyInput.dataset.variantId = String(buyVariant.id);
        buyInput.dataset.planPriceCents = String(buyPrice);

        var buyPriceEl = root.querySelector('[data-rental-purchase-card="buy"] [data-rental-buy-price]');
        if (buyPriceEl) buyPriceEl.textContent = formatMoney(buyPrice);
      }
    }

    var fixedInputs = picker.querySelectorAll('input[data-rental-plan-type="fixed"]');
    fixedInputs.forEach(function (fixedInput) {
      var fixedVariant = resolveSibling(fixedInput.dataset.variantId);
      if (!fixedVariant) return;

      var fixedPrice = displayPriceFor(fixedVariant, isExempt);
      fixedInput.dataset.variantId = String(fixedVariant.id);
      fixedInput.dataset.planPriceCents = String(fixedPrice);

      var weeks = Number(fixedInput.dataset.rentalWeeks);
      if (Number.isFinite(weeks) && weeks > 0) {
        fixedInput.dataset.rentalWeeklyFee = formatMoney(Math.round(fixedPrice / weeks));
      }
      fixedInput.dataset.rentalTotalHire = formatMoney(fixedPrice);

      var fixedPriceEl = fixedInput.closest('.rental-wrapper')?.querySelector('[data-rental-fixed-price]');
      if (fixedPriceEl) fixedPriceEl.textContent = formatMoney(fixedPrice);
    });

    // Auto-renew (recommended): only the checkout-critical fields are synced
    // here — variant id, selling plan id/value, and the initial charge. The
    // visible "Initial cost today | Weekly thereafter" caption is left alone:
    // reproducing it would mean duplicating the localized `t` string template
    // here, and the weekly figure can additionally come from an app metafield
    // (see rental-selector.liquid) that isn't exposed in this DOM for JS to
    // read. It stays correct because the caption itself is only ever set from
    // a full section render (server-rendered, already VAT-aware); this just
    // keeps the actual add-to-cart submission (not the caption text) correct
    // in the narrow window before that next render happens.
    var recommendedInput = picker.querySelector('input[data-rental-plan-type="recommended"]');
    if (recommendedInput) {
      var recommendedVariant = resolveSibling(recommendedInput.dataset.variantId);
      if (recommendedVariant) {
        var allocation =
          recommendedVariant.selling_plan_allocations && recommendedVariant.selling_plan_allocations[0];
        var initialCents = allocation ? allocation.price : recommendedVariant.price;
        var initialPrice = isExempt ? initialCents : Math.round(initialCents * (window.Theme?.vatMultiplier ?? 1.2));
        var sellingPlanId = allocation ? String(allocation.selling_plan_id) : '';

        recommendedInput.dataset.variantId = String(recommendedVariant.id);
        recommendedInput.dataset.sellingPlanId = sellingPlanId;
        recommendedInput.value = sellingPlanId || String(recommendedVariant.id);
        recommendedInput.dataset.planPriceCents = String(initialPrice);
      }
    }
  }

  /**
   * On initial page load: silently syncs the ATC button and hidden radio to the
   * correct VAT state without triggering a section re-render.
   */
  function applyVatStateOnLoad(pickerEl, isExempt) {
    updateRentalPlanDisplay(pickerEl, isExempt);

    var variants = getVariantsFromDom(pickerEl);
    if (!variants) return;

    var vatIndex = getVatOptionIndex(pickerEl);
    if (vatIndex === -1) return;

    var selectedOptions = getSelectedOptions(pickerEl);
    var targetVatValue = isExempt ? 'Yes' : 'No';

    if (selectedOptions[vatIndex] === targetVatValue) return;

    var targetVariant = findTargetVariant(variants, selectedOptions, vatIndex, targetVatValue);
    if (!targetVariant) return;

    var vatFieldset = pickerEl.querySelector('fieldset[data-vat-option]');
    if (vatFieldset) {
      var radio = vatFieldset.querySelector('input[type="radio"][value="' + targetVatValue + '"]');
      if (radio) radio.checked = true;
    }

    var root = getProductRoot(pickerEl);
    updateAtcButton(root, targetVariant, isExempt);
    updateVatProperty(root, isExempt);
  }

  /**
   * Checks the correct hidden VAT radio and fires a change event so that
   * VariantOptionPicker triggers a full section re-render. Also immediately
   * updates the ATC button.
   */
  function applyVatStateOnChange(pickerEl, isExempt) {
    var variants = getVariantsFromDom(pickerEl);
    var targetVatValue = isExempt ? 'Yes' : 'No';

    var root = getProductRoot(pickerEl);
    updateVatProperty(root, isExempt);
    updateRentalPlanDisplay(pickerEl, isExempt);

    if (variants) {
      var vatIndex = getVatOptionIndex(pickerEl);
      if (vatIndex !== -1) {
        var selectedOptions = getSelectedOptions(pickerEl);
        var targetVariant = findTargetVariant(variants, selectedOptions, vatIndex, targetVatValue);
        if (targetVariant) {
          updateAtcButton(root, targetVariant, isExempt);
        }
      }
    }

    var vatFieldset = pickerEl.querySelector('fieldset[data-vat-option]');
    if (!vatFieldset) return;

    var targetRadio = vatFieldset.querySelector('input[type="radio"][value="' + targetVatValue + '"]');
    if (!targetRadio || targetRadio.checked) return;

    targetRadio.checked = true;
    targetRadio.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // ---------------------------------------------------------------------------
  // VatExemptControl custom element
  // Renders the trigger button and keeps its display in sync with VAT state.
  // ---------------------------------------------------------------------------
  if (!customElements.get('vat-exempt-control')) {
    class VatExemptControl extends HTMLElement {
      connectedCallback() {
        var root = (
          this.closest('main-product') ||
          this.closest('product-card-quick-add') ||
          this.closest('[data-section-id]') ||
          this.parentElement
        );
        var picker = root && root.querySelector('variant-option-picker[data-vat-exempt="true"]');

        this._picker = picker || null;

        // Apply current VAT state silently on load
        if (picker) {
          applyVatStateOnLoad(picker, isVatExempt());
        }

        // Sync button display text with current state
        this.updateDisplay(isVatExempt());

        // Listen for state changes confirmed via the modal
        this._stateHandler = function (e) {
          this.updateDisplay(e.detail.isExempt);
          if (this._picker) {
            applyVatStateOnChange(this._picker, e.detail.isExempt);
          }
        }.bind(this);

        document.addEventListener('vat:stateChanged', this._stateHandler);
      }

      updateDisplay(isExempt) {
        var btn = this.querySelector('[data-vat-exempt-trigger]');
        if (!btn) return;
        var status = btn.querySelector('[data-vat-exempt-status]');
        if (status) {
          status.textContent = isExempt
            ? 'VAT Exempt'
            : 'I am eligible for VAT relief';
        }
        btn.classList.toggle('is-exempt', isExempt);
      }

      disconnectedCallback() {
        if (this._stateHandler) {
          document.removeEventListener('vat:stateChanged', this._stateHandler);
        }
        this._picker = null;
        this._stateHandler = null;
      }
    }

    if (!customElements.get('vat-exempt-control')) {
      customElements.define('vat-exempt-control', VatExemptControl);
    }
  }

  // ---------------------------------------------------------------------------
  // VAT exempt modal behaviour
  // Initialises once on DOMContentLoaded. The modal HTML lives in theme.liquid
  // via the vat-exempt-modal snippet and is always present in the DOM.
  // ---------------------------------------------------------------------------
  function initVatModal() {
    var modal = document.querySelector('[data-modal="vat-exempt-modal"]');
    if (!modal) return;

    var radios = modal.querySelectorAll('.vat-exempt-radio');
    var reasonSelect = modal.querySelector('[data-vat-reason-select]');
    var reasonError = modal.querySelector('[data-vat-reason-error]');
    var confirmBtn = modal.querySelector('[data-vat-exempt-confirm]');

    // Show/hide the reason dropdown based on which radio is checked
    function updateReasonVisibility() {
      var vatRadio = modal.querySelector('.vat-exempt-radio[value="true"]');
      if (reasonSelect) {
        reasonSelect.style.display = (vatRadio && vatRadio.checked) ? 'block' : 'none';
      }
      clearReasonError();
    }

    function clearReasonError() {
      if (reasonSelect) reasonSelect.classList.remove('error');
      if (reasonError) reasonError.hidden = true;
    }

    radios.forEach(function (r) {
      r.addEventListener('change', updateReasonVisibility);
    });

    reasonSelect && reasonSelect.addEventListener('change', clearReasonError);

    // Pre-populate state from localStorage whenever the modal opens
    var observer = new MutationObserver(function () {
      if (modal.classList.contains('active')) {
        var exempt = isVatExempt();
        radios.forEach(function (r) {
          r.checked = (r.value === String(exempt));
        });
        updateReasonVisibility();
        if (reasonSelect) {
          reasonSelect.value = localStorage.getItem(VAT_REASON_KEY) || '';
        }
      }
    });
    observer.observe(modal, { attributes: true, attributeFilter: ['class'] });

    // Confirm button handler
    confirmBtn && confirmBtn.addEventListener('click', async function () {
      var selected = modal.querySelector('.vat-exempt-radio:checked');
      if (!selected) return;

      var isExempt = selected.value === 'true';
      var reason = (isExempt && reasonSelect) ? reasonSelect.value : '';

      if (isExempt && !reason) {
        if (reasonSelect) reasonSelect.classList.add('error');
        if (reasonError) reasonError.hidden = false;
        if (reasonSelect) reasonSelect.focus();
        return;
      }

      clearReasonError();

      // Save reason
      localStorage.setItem(VAT_REASON_KEY, reason);

      // Disable confirm button and show loading state
      var originalText = confirmBtn.textContent;
      confirmBtn.disabled = true;
      confirmBtn.classList.add('is-loading');
      confirmBtn.textContent = 'Updating...';

      // Persist + swap cart
      if (window.vatExemption) {
        await window.vatExemption.set(isExempt);
      } else {
        localStorage.setItem(VAT_STORAGE_KEY, String(isExempt));
      }

      // Close modal once work is done
      var mc = window.__quickfireModalController;
      if (mc) mc.hideModal('vat-exempt-modal');

      confirmBtn.disabled = false;
      confirmBtn.classList.remove('is-loading');
      confirmBtn.textContent = originalText;

      // Notify all VatExemptControl instances
      document.dispatchEvent(new CustomEvent('vat:stateChanged', {
        detail: { isExempt: isExempt, reason: reason }
      }));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initVatModal);
  } else {
    initVatModal();
  }

})();
